import { ReactNode, useEffect, useMemo, useState } from "react"
import {
  Eye,
  Plus,
  Megaphone,
  X,
  Sparkles,
  Tags,
  BadgeCheck,
  Phone,
  Wallet,
  Users,
  ArrowRight,
  UserPlus,
} from "lucide-react"
import { allClientsData, allGeneralClientsData } from "../../store/actions"
import { useDispatch, useSelector } from "react-redux"
import { isCatalogStore, isPropertyStore } from "../../utils/storeCategory"
import { getStoreExperience } from "../../utils/storeExperience"
import ClientDetailsModal from "./ClientDetailsModal"
import { getClientDetailsData, getGeneralClientDetails } from "../../api/products/get"
import { BASE_URL } from "../../api/api"
import { useNavigate } from "react-router-dom"
import { createManualCustomer, getManualCustomers } from "../../api/auth/crm"
import { AnimatePresence, motion } from "framer-motion"
import { toast } from "react-hot-toast"

type MergedClient = {
  name: string
  phone: string
  totalDebt: number
  purchases: number
  tags?: string[]
  notes?: string
  consentSms?: boolean
  isManual?: boolean
}

export default function Clients() {
  const { userdata, allclientsdata, generalclientsdata } = useSelector((state: any) => state.UserReducer)

  const [showDebtorsOnly, setShowDebtorsOnly] = useState(false)
  const [selectedClient, setSelectedClient] = useState<any | null>(null)
  const [clientDetails, setClientDetails] = useState<any | null>(null)
  const [page, setPage] = useState(1)
  const [openAddModal, setOpenAddModal] = useState(false)
  const [manualCustomers, setManualCustomers] = useState<any[]>([])
  const limit = 6

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { store } = userdata
  const isGeneralStore = isCatalogStore(store)
  const propertyStore = isPropertyStore(store)
  const experience = getStoreExperience(store)

  const storeId = store?._id
  const pagination = allclientsdata?.pagination || {}
  const { totalPages = 1 } = pagination
  const generalPagination = generalclientsdata?.pagination || {}

  useEffect(() => {
    if (!storeId) return
    if (isGeneralStore) {
      dispatch(allGeneralClientsData(storeId, page, limit, showDebtorsOnly))
    } else {
      dispatch(allClientsData(storeId, page, limit, showDebtorsOnly))
    }
  }, [dispatch, storeId, page, showDebtorsOnly, isGeneralStore])

  useEffect(() => {
    if (!storeId) return
    const loadManualCustomers = async () => {
      try {
        const response = await getManualCustomers(storeId)
        setManualCustomers(response?.data || [])
      } catch (e: any) {
        console.error(e)
        toast.error(e?.message || "Impossible de charger les clients manuels")
      }
    }

    loadManualCustomers()
  }, [storeId])

  const mergedClients: MergedClient[] = useMemo(() => {
    const apiSource = isGeneralStore ? generalclientsdata?.data || [] : allclientsdata?.data || []
    const apiClients = apiSource.map((c: any) => ({
      name: c.name || c.clientName || "Client",
      phone: c.phone || c.clientPhone,
      totalDebt: Number(c.totalDebt || 0),
      purchases: Number(c.purchases || 0),
      isManual: false,
    }))

    const manualAsClients = manualCustomers.map((m: any) => ({
      name: m.name,
      phone: m.phone,
      totalDebt: 0,
      purchases: 0,
      tags: m.tags || [],
      notes: m.notes || "",
      consentSms: !!m.consentSms,
      isManual: true,
    }))

    const byPhone = new Map<string, MergedClient>()

    for (const c of [...apiClients, ...manualAsClients]) {
      if (!c.phone) continue
      const existing = byPhone.get(c.phone)
      if (!existing) {
        byPhone.set(c.phone, c)
      } else {
        byPhone.set(c.phone, {
          ...existing,
          name: existing.name || c.name,
          totalDebt: Math.max(Number(existing.totalDebt || 0), Number(c.totalDebt || 0)),
          purchases: Math.max(Number(existing.purchases || 0), Number(c.purchases || 0)),
          tags: existing.tags || c.tags,
          notes: existing.notes || c.notes,
          consentSms: existing.consentSms ?? c.consentSms,
          isManual: existing.isManual && c.isManual,
        })
      }
    }
    return Array.from(byPhone.values())
  }, [isGeneralStore, generalclientsdata?.data, allclientsdata?.data, manualCustomers])

  const filteredClients = useMemo(
    () => (showDebtorsOnly ? mergedClients.filter((c) => c.totalDebt > 0) : mergedClients),
    [mergedClients, showDebtorsOnly]
  )

  const totalDebtAmount = useMemo(
    () => filteredClients.reduce((sum, client) => sum + Number(client.totalDebt || 0), 0),
    [filteredClients]
  )
  const debtorCount = useMemo(
    () => filteredClients.filter((client) => Number(client.totalDebt || 0) > 0).length,
    [filteredClients]
  )
  const manualCount = useMemo(
    () => filteredClients.filter((client) => client.isManual).length,
    [filteredClients]
  )
  const totalPurchases = useMemo(
    () => filteredClients.reduce((sum, client) => sum + Number(client.purchases || 0), 0),
    [filteredClients]
  )

  const pageTitle = propertyStore ? "Clients immobilier" : "Clients"
  const pageSubtitle = propertyStore
    ? "Acquereurs, locataires, prospects et historiques de transactions reunis dans un espace plus premium."
    : "CRM boutique repense pour suivre vos clients, relances et campagnes avec plus de clarté."
  const createLabel = propertyStore ? "Nouveau contact" : "Nouveau client"
  const debtToggleLabel = showDebtorsOnly
    ? propertyStore
      ? "Tous les contacts"
      : "Tous les clients"
    : propertyStore
      ? "Contacts avec encours"
      : "Clients débiteurs"
  const emptyLabel = propertyStore ? "Aucun contact immobilier" : "Aucun client"
  const accentClass = propertyStore ? "from-emerald-700 via-teal-700 to-cyan-700" : "from-sky-600 via-blue-700 to-indigo-900"
  const accentSoftClass = propertyStore ? "from-emerald-50 via-white to-cyan-50" : "from-blue-50 via-white to-cyan-50"
  const accentButtonClass = propertyStore
    ? "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-200"
    : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-200"
  const toggleActiveClass = propertyStore
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : "border-blue-200 bg-blue-50 text-blue-700"

  const resolveClientTypeLabel = (client: MergedClient) => {
    if (propertyStore) {
      return client.isManual ? "Prospect immobilier" : "Client immobilier"
    }

    return client.isManual ? "Ajout manuel" : "Client vente"
  }

  const resolveClientTypeClass = (client: MergedClient) => {
    if (propertyStore) {
      return client.isManual ? "bg-emerald-100 text-emerald-700" : "bg-cyan-100 text-cyan-700"
    }

    return client.isManual ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"
  }

  const handleViewClient = async (client: MergedClient) => {
    const phone = client.phone

    if (client.isManual && client.totalDebt <= 0) {
      setSelectedClient(client)
      setClientDetails({
        client: {
          name: client.name,
          phone,
        },
        summary: {
          totalSales: 0,
          totalPaid: 0,
          totalDebt: 0,
        },
        sales: [],
      })
      return
    }

    try {
      setSelectedClient(client)
      const data = isGeneralStore
        ? await getGeneralClientDetails(storeId, phone)
        : await getClientDetailsData(storeId, phone)
      setClientDetails(data)
    } catch (e) {
      console.error(e)
    }
  }

  const handleAddManualClient = async (payload: {
    name: string
    phone: string
    tags: string[]
    notes: string
    consentSms: boolean
  }) => {
    if (!storeId) return
    try {
      await createManualCustomer(storeId, payload)
      const response = await getManualCustomers(storeId)
      setManualCustomers(response?.data || [])
      toast.success("Client enregistré")
    } catch (e: any) {
      console.error(e)
      toast.error(e?.message || "Impossible d'enregistrer le client")
      throw e
    }
  }

  return (
    <>
      <div className="space-y-8">
        <section className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br p-6 text-white shadow-xl sm:p-7 ${accentClass}`}>
          <div className="pointer-events-none absolute -top-14 -right-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-14 -left-8 h-36 w-36 rounded-full bg-cyan-200/20 blur-3xl" />

          <div className="relative flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> {propertyStore ? experience.badge : "CRM Lounge"}
              </p>
              <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{pageTitle}</h1>
              <p className="mt-2 text-sm text-white/90 sm:text-[15px]">{pageSubtitle}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => setOpenAddModal(true)}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-slate-900/10 transition focus:outline-none focus:ring-4 ${accentButtonClass}`}
                >
                  <Plus size={16} />
                  {createLabel}
                </button>
                <button
                  onClick={() => navigate("/campaigns")}
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/15"
                >
                  <Megaphone size={16} />
                  Groupes SMS
                </button>
                <button
                  onClick={() => {
                    setPage(1)
                    setShowDebtorsOnly(!showDebtorsOnly)
                  }}
                  className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-medium backdrop-blur-sm transition ${showDebtorsOnly ? "border-white/20 bg-white text-slate-800" : "border-white/20 bg-white/10 text-white hover:bg-white/15"}`}
                >
                  <Wallet size={16} />
                  {debtToggleLabel}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
              <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-sm">
                <p className="text-xs uppercase tracking-[0.12em] text-white/70">Clients visibles</p>
                <p className="mt-1 text-2xl font-semibold">{filteredClients.length}</p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-sm">
                <p className="text-xs uppercase tracking-[0.12em] text-white/70">Dette active</p>
                <p className="mt-1 text-2xl font-semibold">{totalDebtAmount.toLocaleString()} FCFA</p>
              </div>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <ClientMetricCard
            label={propertyStore ? "Portefeuille visible" : "Clients suivis"}
            value={filteredClients.length}
            icon={<Users size={18} />}
            gradient={propertyStore ? "from-emerald-600 to-teal-500" : "from-blue-600 to-cyan-500"}
          />
          <ClientMetricCard
            label={propertyStore ? "Contacts en attente" : "Clients débiteurs"}
            value={debtorCount}
            icon={<Wallet size={18} />}
            gradient="from-rose-600 to-orange-500"
          />
          <ClientMetricCard
            label={propertyStore ? "Prospects ajoutés" : "Ajouts manuels"}
            value={manualCount}
            icon={<UserPlus size={18} />}
            gradient="from-violet-600 to-fuchsia-500"
          />
          <ClientMetricCard
            label={propertyStore ? "Dossiers liés" : "Achats cumulés"}
            value={totalPurchases}
            icon={<Sparkles size={18} />}
            gradient="from-slate-700 to-slate-500"
          />
        </div>

        <div className="hidden overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm md:block">
          <div className={`border-b border-slate-100 bg-gradient-to-r px-6 py-5 ${accentSoftClass}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">Répertoire clients</p>
                <p className="mt-1 text-xs text-slate-500">
                  Une vue plus claire des profils, dettes et contacts actifs.
                </p>
              </div>
              <div
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${showDebtorsOnly ? toggleActiveClass : "border-slate-200 bg-white text-slate-600"}`}
              >
                <span className={`h-2 w-2 rounded-full ${showDebtorsOnly ? (propertyStore ? "bg-emerald-500" : "bg-blue-500") : "bg-slate-300"}`} />
                {showDebtorsOnly ? "Filtre débiteurs actif" : "Vue complète"}
              </div>
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-6 py-3">Client</th>
                <th className="px-6 py-3">Contact</th>
                <th className="px-6 py-3">{propertyStore ? "Encours" : "Dette totale"}</th>
                <th className="px-6 py-3">Profil</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredClients.map((c: MergedClient) => (
                <tr key={c.phone} className="border-t border-slate-100 transition hover:bg-slate-50/80">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br text-sm font-semibold text-white ${c.isManual ? "from-violet-500 to-fuchsia-500" : propertyStore ? "from-emerald-500 to-cyan-500" : "from-blue-500 to-cyan-500"}`}>
                        {getInitials(c.name)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{c.name}</p>
                        <p className="text-xs text-slate-500">
                          {Number(c.purchases || 0)} {propertyStore ? "dossier(s)" : "achat(s)"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      <Phone size={13} />
                      {c.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {c.totalDebt > 0 ? (
                      <span className="inline-flex rounded-full bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700">
                        {c.totalDebt.toLocaleString()} FCFA
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                        0 FCFA
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${resolveClientTypeClass(c)}`}>
                      {resolveClientTypeLabel(c)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleViewClient(c)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 transition hover:bg-slate-100"
                    >
                      <Eye size={16} />
                      Voir
                    </button>
                  </td>
                </tr>
              ))}

              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                    {emptyLabel}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 md:hidden">
          {filteredClients.map((c: MergedClient) => (
            <div key={c.phone} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className={`bg-gradient-to-r px-4 py-4 ${accentSoftClass}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br text-sm font-semibold text-white ${c.isManual ? "from-violet-500 to-fuchsia-500" : propertyStore ? "from-emerald-500 to-cyan-500" : "from-blue-500 to-cyan-500"}`}>
                      {getInitials(c.name)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.phone}</p>
                    </div>
                  </div>

                  {c.totalDebt > 0 ? (
                    <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700">Dette</span>
                  ) : (
                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">À jour</span>
                  )}
                </div>
              </div>

              <div className="space-y-3 p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Type</span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${resolveClientTypeClass(c)}`}>
                    {resolveClientTypeLabel(c)}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{propertyStore ? "Dossiers" : "Achats"}</span>
                  <span className="font-semibold text-slate-800">{Number(c.purchases || 0)}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{propertyStore ? "Encours" : "Dette totale"}</span>
                  <span className={`font-semibold ${c.totalDebt > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {c.totalDebt.toLocaleString()} FCFA
                  </span>
                </div>

                <button
                  onClick={() => handleViewClient(c)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 py-2.5 text-slate-700 transition hover:bg-slate-50"
                >
                  Voir détails
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}

          {filteredClients.length === 0 && (
            <div className={`rounded-3xl border border-dashed border-slate-300 bg-gradient-to-br p-8 text-center ${accentSoftClass}`}>
              <p className="text-base font-semibold text-slate-700">{emptyLabel}</p>
              <p className="mt-1 text-sm text-slate-500">
                Ajoutez un contact pour commencer à structurer votre portefeuille client.
              </p>
            </div>
          )}
        </div>

        {filteredClients.length === 0 && (
          <div className={`hidden rounded-3xl border border-dashed border-slate-300 bg-gradient-to-br p-10 text-center md:block ${accentSoftClass}`}>
            <p className="text-lg font-semibold text-slate-700">{emptyLabel}</p>
            <p className="mt-2 text-sm text-slate-500">
              Ajoutez un contact ou désactivez le filtre débiteurs pour retrouver l'ensemble du fichier client.
            </p>
          </div>
        )}

        {(isGeneralStore ? generalPagination?.totalPages : totalPages) > 1 && (
          <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">
              Page <strong className="text-slate-700">{page}</strong> sur{" "}
              <strong className="text-slate-700">{isGeneralStore ? generalPagination?.totalPages : totalPages}</strong>
            </p>

            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-xl border border-slate-200 px-4 py-2 text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Précédent
              </button>

              <button
                disabled={page === (isGeneralStore ? generalPagination?.totalPages : totalPages)}
                onClick={() => setPage((p) => Math.min(isGeneralStore ? generalPagination?.totalPages : totalPages, p + 1))}
                className={`rounded-xl px-4 py-2 text-white transition disabled:opacity-50 ${propertyStore ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedClient && (
        <ClientDetailsModal
          clientData={clientDetails}
          store={store}
          onClose={() => setSelectedClient(null)}
          onOpenSale={(sale) => {
            window.open(
              isGeneralStore
                ? `${BASE_URL}/api/gestock/general/invoice/${sale._id}/${storeId}`
                : `${BASE_URL}/api/gestock/invoice/${sale._id}/${storeId}`,
              "_blank"
            )
          }}
        />
      )}

      <AddManualClientModal
        isOpen={openAddModal}
        propertyStore={propertyStore}
        experience={experience}
        onClose={() => setOpenAddModal(false)}
        onSubmit={async (payload) => {
          await handleAddManualClient(payload)
          setOpenAddModal(false)
        }}
      />
    </>
  )
}

function ClientMetricCard({
  label,
  value,
  icon,
  gradient,
}: {
  label: string
  value: string | number
  icon: ReactNode
  gradient: string
}) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className={`flex items-start justify-between bg-gradient-to-r px-4 py-4 text-white ${gradient}`}>
        <div className="rounded-2xl bg-white/15 p-2.5 backdrop-blur-sm">{icon}</div>
        <Sparkles size={16} className="text-white/75" />
      </div>
      <div className="px-4 py-4">
        <p className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  )
}

function getInitials(name?: string) {
  if (!name) return "CL"
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return (
    parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "CL"
  )
}

function AddManualClientModal({
  isOpen,
  propertyStore,
  experience,
  onClose,
  onSubmit,
}: {
  isOpen: boolean
  propertyStore: boolean
  experience: ReturnType<typeof getStoreExperience>
  onClose: () => void
  onSubmit: (payload: { name: string; phone: string; tags: string[]; notes: string; consentSms: boolean }) => Promise<void>
}) {
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [tagsText, setTagsText] = useState("")
  const [notes, setNotes] = useState("")
  const [consentSms, setConsentSms] = useState(true)
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-white/40 bg-white shadow-2xl xl:ml-52"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
          >
            <div className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full bg-blue-400/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-cyan-300/25 blur-3xl" />

            <div className={`relative border-b border-slate-100 bg-gradient-to-r px-5 py-4 text-white ${propertyStore ? "from-emerald-700 via-teal-700 to-cyan-700" : "from-blue-600 to-cyan-600"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] uppercase tracking-[0.12em]">
                    <Sparkles size={12} /> {propertyStore ? experience.badge : "CRM"}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold">{propertyStore ? "Nouveau contact immobilier" : "Nouveau client"}</h3>
                  <p className="text-sm text-white/90">
                    {propertyStore
                      ? "Ajoutez un acquereur, locataire ou prospect a suivre."
                      : "Ajoutez un contact pour vos campagnes et suivis."}
                  </p>
                </div>
                <button onClick={onClose} className="rounded-xl p-2 transition hover:bg-white/20">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="relative space-y-4 px-5 py-5">
              {error && <div className="rounded-xl bg-red-50 p-2 text-sm text-red-600">{error}</div>}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={propertyStore ? "Nom du contact *" : "Nom complet *"}
                  className={`rounded-xl border border-slate-200 px-3 py-2.5 outline-none ${propertyStore ? "focus:ring-4 focus:ring-emerald-100" : "focus:ring-4 focus:ring-blue-100"}`}
                />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Téléphone *"
                  className={`rounded-xl border border-slate-200 px-3 py-2.5 outline-none ${propertyStore ? "focus:ring-4 focus:ring-emerald-100" : "focus:ring-4 focus:ring-blue-100"}`}
                />
              </div>

              <div className="relative">
                <Tags size={16} className="absolute left-3 top-3 text-slate-400" />
                <input
                  value={tagsText}
                  onChange={(e) => setTagsText(e.target.value)}
                  placeholder={propertyStore ? "Tags (Locataire, Acheteur, Investisseur)" : "Tags (VIP, WhatsApp, Fidèle)"}
                  className={`w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 outline-none ${propertyStore ? "focus:ring-4 focus:ring-emerald-100" : "focus:ring-4 focus:ring-blue-100"}`}
                />
              </div>

              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder={propertyStore ? "Budget, zone recherchee, conditions de location..." : "Notes client"}
                className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none ${propertyStore ? "focus:ring-4 focus:ring-emerald-100" : "focus:ring-4 focus:ring-blue-100"}`}
              />

              <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <input type="checkbox" checked={consentSms} onChange={(e) => setConsentSms(e.target.checked)} />
                <BadgeCheck size={16} className="text-emerald-600" />
                Ce client accepte les campagnes SMS
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2 hover:bg-slate-50">
                  Annuler
                </button>
                <button
                  disabled={submitting}
                  onClick={() => {
                    if (!name.trim() || !phone.trim()) {
                      setError("Nom et téléphone sont obligatoires")
                      return
                    }
                    const tags = tagsText
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean)
                    setSubmitting(true)
                    onSubmit({ name: name.trim(), phone: phone.trim(), tags, notes: notes.trim(), consentSms })
                      .then(() => {
                        setName("")
                        setPhone("")
                        setTagsText("")
                        setNotes("")
                        setConsentSms(true)
                        setError("")
                      })
                      .catch((e: any) => {
                        setError(e?.message || "Impossible d'ajouter ce client")
                      })
                      .finally(() => setSubmitting(false))
                  }}
                  className={`rounded-xl px-4 py-2 text-white disabled:opacity-60 ${propertyStore ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {submitting ? "Enregistrement..." : propertyStore ? "Enregistrer le contact" : "Enregistrer le client"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
